import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Handshake, CheckCircle2, ArrowRight, Clock, AlertCircle, FileSearch } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Phase4Verhandlung = () => {
  const verhandlungsSchritte = [
    { titel: 'Angebots-Eingang & Erstprüfung', details: 'Schriftliches Angebot mit Preis, Bedingungen, Finanzierungsnachweis, Käufer-Referenzen', pflicht: true },
    { titel: 'Käufer-Bonitätsprüfung', details: 'Eigenkapitalnachweis (mind. 30%), Bankbestätigung, bisherige Transaktionen, Background-Check', pflicht: true },
    { titel: 'Due Diligence-Phase starten', details: 'Käufer prüft alle Unterlagen im Datenraum (2-4 Wochen Frist)', pflicht: true },
    { titel: 'Q&A-Runden organisieren', details: 'Käufer stellt 20-50 Fragen schriftlich, Eigentümer beantwortet mit Makler-Unterstützung', pflicht: true },
    { titel: 'Objektbesichtigung (Detail)', details: 'Käufer bringt Gutachter, Architekt, ggf. Banker mit (2-3 Stunden)', pflicht: true },
    { titel: 'Mieter-Gespräch ermöglichen', details: 'Käufer möchte Mieter persönlich kennenlernen (diskret, mit Vorlauf)', pflicht: true },
    { titel: 'Preisverhandlung führen', details: 'Käufer fordert meist 5-10% Rabatt nach Due Diligence → Verhandlungstaktik entscheidend', pflicht: true },
    { titel: 'Letter of Intent (LOI) unterzeichnen', details: 'Absichtserklärung mit Preis, Zeitplan, Bedingungen (rechtlich nicht bindend)', pflicht: true },
    { titel: 'Kaufvertragsentwurf prüfen', details: 'Notar erstellt Entwurf, beide Seiten prüfen & kommentieren (1-2 Iterationen)', pflicht: true },
    { titel: 'Parallelverhandlungen?', details: 'Mit mehreren Käufern gleichzeitig verhandeln für Druck? Oder Exklusivität geben?', pflicht: false }
  ];

  const verantwortlichkeiten = [
    { wer: 'Eigentümer', aufgaben: ['Realistische Schmerzgrenze definieren (Mindestpreis)', 'Schnelle Antworten auf Due Diligence-Fragen', 'Entscheidung bei Gegenangeboten (max. 48h)', 'Anwalt für Kaufvertragsprüfung beauftragen'] },
    { wer: 'RetailInvest', aufgaben: ['Angebote bewerten & vergleichen (nicht nur Preis!)', 'Käufer-Bonität prüfen (Referenzen, Eigenkapital)', 'Verhandlung führen (Preisargumentation, Bedingungen)', 'Due Diligence-Prozess koordinieren', 'LOI-Erstellung', 'Parallelverhandlungen managen (falls mehrere Käufer)'] },
    { wer: 'Anwalt (Notar-Vorbereitung)', aufgaben: ['Kaufvertragsentwurf prüfen', 'Haftungsausschlüsse verhandeln', 'Gewährleistung regeln', 'Kosten: € 2.000-5.000'] }
  ];

  const typischeProbleme = [
    { problem: 'Käufer findet in Due Diligence "versteckte Mängel" und fordert 10-15% Rabatt', loesung: 'Nur berechtigte Mängel akzeptieren (Gutachten vorlegen lassen). Alternativ: Verkäufer behebt Mangel VOR Closing oder akzeptiert 50% des geforderten Rabatts als Kompromiss.' },
    { problem: 'Käufer will Kaufpreis um 6 Monate stunden (erst zahlen nach Mieteingang)', loesung: 'Nur gegen Bankbürgschaft (100% des Kaufpreises) oder Zinsen (4-6% p.a.) akzeptieren. Risiko: Käufer springt ab.' },
    { problem: 'Zwei Käufer bieten fast gleichen Preis – welcher ist besser?', loesung: 'Nicht nur Preis zählt! Prüfen: Finanzierungssicherheit (100% Eigenkapital > 70% Fremdkapital), Timing (schnellerer Abschluss = besser), Bedingungen (weniger Auflagen = besser), Seriosität (Referenzen).' },
    { problem: 'Käufer verlangt Exklusivität für 6 Wochen – aber bietet noch kein finales Angebot', loesung: 'Nur gegen "Break Fee" (1-2% des Kaufpreises, verfallbar wenn Käufer abspringt). Oder Exklusivität auf 3 Wochen begrenzen + harte Deadlines für LOI und Vertragsentwurf.' }
  ];

  const dueDiligenceCheckliste = [
    { kategorie: 'Rechtlich', punkte: ['Grundbucheintrag ohne Lasten?', 'Baugenehmigung vollständig?', 'Mietvertrag rechtssicher?', 'Keine laufenden Rechtsstreitigkeiten?'] },
    { kategorie: 'Kaufmännisch', punkte: ['Mieteingang letzten 12 Monate lückenlos?', 'Nebenkosten korrekt abgerechnet?', 'Keine verdeckten Rückstände?', 'WAULT korrekt berechnet?'] },
    { kategorie: 'Technisch', punkte: ['Gebäudezustand (Dach, Fassade, Technik)?', 'Energieausweis aktuell?', 'Altlasten im Boden?', 'Modernisierungsstau (€/qm)?'] },
    { kategorie: 'Steuerlich', punkte: ['Spekulationsfrist abgelaufen?', 'Grunderwerbsteuer (Käufer-Seite)?', 'Abschreibung (AfA) möglich?', 'Vorsteuerabzug?'] }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Phase 4: Verhandlung & Due Diligence – Verkaufsprozess Handelsimmobilie" description="Detaillierte Anleitung zu Phase 4: Angebotsprüfung, Käuferprüfung, Due Diligence-Prozess, Verhandlungstaktik, Letter of Intent." url="/ablauf/phase-4-verhandlung" />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/ablauf" className="text-sm hover:underline mb-4 inline-block" style={{ color: 'var(--navy-600)' }}>← Zurück zur Übersicht</Link>
          
          <div className="flex items-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)' }}>
              <span className="text-4xl font-bold text-white">04</span>
            </div>
            <div>
              <div className="w-16 h-1 bg-gradient-to-r from-[#0F2744] to-[#2563A0] mb-2"></div>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--navy-900)' }}>Verhandlung & Due Diligence</h1>
              <div className="flex items-center gap-2 mt-2">
                <Clock className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />
                <p className="text-lg font-semibold" style={{ color: 'var(--gray-700)' }}>Dauer: 3-6 Wochen</p>
              </div>
            </div>
          </div>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Warum ist diese Phase so wichtig?</h2>
            <p className="text-lg mb-4 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              <strong>Hier entscheidet sich der finale Verkaufspreis.</strong> Käufer nutzen Due Diligence systematisch, um Rabatte zu verhandeln ("Wir haben Mängel gefunden..."). Eine professionelle Vorbereitung und klare Verhandlungsstrategie kann 50.000-200.000 € Differenz bedeuten.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              <strong>Due Diligence ist keine Schikane, sondern Standard.</strong> Institutionelle Käufer müssen ihre Gremien überzeugen – daher prüfen sie akribisch. Je besser Sie vorbereitet sind, desto schneller und reibungsloser läuft der Prozess. Verzögerungen kosten Geld (Opportunitätskosten, Alternativkäufer springen ab).
            </p>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Verhandlungs-Checkliste</h2>
            <div className="space-y-4">
              {verhandlungsSchritte.map((s, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: s.pflicht ? 'var(--gold-600)' : 'var(--navy-400)' }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold" style={{ color: 'var(--navy-800)' }}>{s.titel}</h3>
                      {s.pflicht && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                          Pflicht
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>{s.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Due Diligence: Was prüft der Käufer?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dueDiligenceCheckliste.map((d, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{d.kategorie}</h3>
                  <ul className="space-y-2">
                    {d.punkte.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Verantwortlichkeiten</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {verantwortlichkeiten.map((v, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: i === 1 ? 'var(--gold-100)' : 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{v.wer}</h3>
                  <ul className="space-y-3">
                    {v.aufgaben.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <span className="font-bold" style={{ color: 'var(--gold-600)' }}>•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Typische Probleme & Lösungen</h2>
            <div className="space-y-6">
              {typischeProbleme.map((item, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-red-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                      <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Problem:</h3>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-800)' }}>{item.problem}</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2" style={{ color: 'var(--success-600)' }}>Lösung:</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.loesung}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Expertentipp: Verhandlungstaktik</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--success-700)' }}>✓ Do's</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• Mehrere Käufer parallel verhandeln (Druck erzeugen)</li>
                  <li>• Deadline setzen: "Angebot gilt bis Freitag 18 Uhr"</li>
                  <li>• Bei Rabattforderung: Gegenleistung verlangen (schnellerer Closing, keine Auflagen)</li>
                  <li>• LOI unterschreiben, aber Kaufvertrag durch Anwalt prüfen lassen</li>
                  <li>• "Good Cop / Bad Cop": Makler verhandelt hart, Eigentümer bleibt freundlich</li>
                </ul>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--error-700)' }}>✗ Don'ts</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• Erstes Angebot sofort annehmen (immer nachverhandeln)</li>
                  <li>• Exklusivität ohne "Break Fee" geben (Käufer nutzt Zeit, springt ab)</li>
                  <li>• Emotionale Entscheidungen ("Den mag ich nicht" → Geld zählt)</li>
                  <li>• Zu viel verraten ("Ich brauche das Geld dringend" → schwache Position)</li>
                  <li>• Ohne Anwalt unterschreiben (Kaufvertrag hat 20+ kritische Klauseln)</li>
                </ul>
              </div>
            </div>
          </Card>

          <div className="flex gap-4">
            <Link to="/ablauf/phase-3-vermarktung" className="flex-1">
              <Button variant="outline" className="w-full font-semibold py-6" style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}>
                ← Zurück zu Phase 3
              </Button>
            </Link>
            <Link to="/ablauf/phase-5-notar" className="flex-1">
              <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                Weiter zu Phase 5: Notartermin
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Phase4Verhandlung;
